// Phase 13 — Unified inbox for pending money items (manager / accounts)
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, ChevronRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { StatusPill } from "@/pages/field/money/_helpers";
import PageHeader from "@/components/PageHeader";

const TYPE_LABEL = {
  payment: "Payment", claim: "Claim", sale_return: "Return", expense: "Expense",
};

const TYPE_COLOR = {
  payment: "bg-sky-50 border-sky-200 text-sky-800",
  claim: "bg-amber-50 border-amber-200 text-amber-800",
  sale_return: "bg-purple-50 border-purple-200 text-purple-800",
  expense: "bg-emerald-50 border-emerald-200 text-emerald-800",
};

// Action matrix per (entity_type, current_status) -> [ {label, endpoint, requires_notes} ]
function actionsFor(item) {
  const t = item.entity_type; const s = item.status;
  if (t === "payment" && s === "submitted") return [
    { code: "verify", label: "Verify", url: `/payments/${item.id}/verify` },
    { code: "discrepancy", label: "Flag Discrepancy", url: `/payments/${item.id}/discrepancy`, requires_notes: true },
    { code: "reject", label: "Reject", url: `/payments/${item.id}/reject`, requires_notes: true, danger: true },
  ];
  if (t === "claim" && s === "submitted") return [
    { code: "asm-approve", label: "ASM Approve", url: `/claims/${item.id}/asm-approve` },
    { code: "asm-reject", label: "Reject", url: `/claims/${item.id}/asm-reject`, requires_notes: true, danger: true },
  ];
  if (t === "claim" && s === "asm_approved") return [
    { code: "accounts-approve", label: "Accounts Approve", url: `/claims/${item.id}/accounts-approve` },
    { code: "accounts-reject", label: "Reject", url: `/claims/${item.id}/accounts-reject`, requires_notes: true, danger: true },
    { code: "mark-paid", label: "Mark Paid", url: `/claims/${item.id}/mark-paid` },
  ];
  if (t === "sale_return" && s === "submitted") return [
    { code: "asm-approve", label: "ASM Approve", url: `/sale-returns/${item.id}/asm-approve` },
    { code: "asm-reject", label: "Reject", url: `/sale-returns/${item.id}/asm-reject`, requires_notes: true, danger: true },
  ];
  if (t === "sale_return" && s === "asm_approved") return [
    { code: "accounts-approve", label: "Accounts Approve", url: `/sale-returns/${item.id}/accounts-approve` },
    { code: "accounts-reject", label: "Reject", url: `/sale-returns/${item.id}/accounts-reject`, requires_notes: true, danger: true },
  ];
  if (t === "expense" && s === "submitted") return [
    { code: "approve", label: "Approve", url: `/expenses/${item.id}/approve` },
    { code: "reject", label: "Reject", url: `/expenses/${item.id}/reject`, requires_notes: true, danger: true },
  ];
  return [];
}

export default function MoneyInboxPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);   // item selected for detail
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const [confirmAction, setConfirmAction] = useState(null); // {url, requires_notes, label}
  const [notes, setNotes] = useState("");

  async function reload() {
    setLoading(true);
    try {
      const { data } = await api.get("/money-inbox");
      setItems(data.items || []); setCounts(data.counts || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() =>
    filter === "all" ? items : items.filter(x => x.entity_type === filter),
    [items, filter]);

  async function openDetail(item) {
    setActive(item);
    setDetail(null);
    // fetch full detail per module
    const url = (
      item.entity_type === "payment"     ? `/payments/${item.id}` :
      item.entity_type === "claim"       ? `/claims/${item.id}` :
      item.entity_type === "sale_return" ? `/sale-returns/${item.id}` :
                                            `/expenses/${item.id}`
    );
    try {
      const { data } = await api.get(url);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function performAction() {
    if (!confirmAction) return;
    if (confirmAction.requires_notes && !notes.trim()) {
      toast.error("Reason required"); return;
    }
    setBusy(true);
    try {
      await api.post(confirmAction.url, { reviewer_notes: notes || null });
      toast.success("Done");
      setConfirmAction(null); setNotes(""); setActive(null); setDetail(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="p-6" data-testid="money-inbox-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Money" }, { label: "Money Inbox" }]}
        title="Money Inbox"
        subtitle="Distributor payments, claims and expense receipts pending your verification."
        favorite={{ key: "admin.money-inbox", type: "screen", label: "Money Inbox", route: "/manager/money-inbox" }}
        testid="moneyinboxpage-page-header"
      />

      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-2xl font-semibold">Money Inbox</h1>
        <Button variant="ghost" size="sm" onClick={reload}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      <div className="flex gap-2 mb-4 text-sm">
        {["all", "payment", "claim", "sale_return", "expense"].map(t => {
          const count = t === "all" ? items.length : (counts[t] || 0);
          return (
            <button key={t}
                    data-testid={`inbox-filter-${t}`}
                    className={`px-3 py-1.5 rounded-full border ${filter === t ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-300"}`}
                    onClick={() => setFilter(t)}>
              {t === "all" ? "All" : TYPE_LABEL[t]} ({count})
            </button>
          );
        })}
      </div>

      {loading && <div className="text-neutral-500 text-sm">Loading…</div>}
      {!loading && filtered.length === 0 && (
        <div className="text-neutral-500 text-sm border border-dashed rounded-lg p-10 text-center">Nothing pending.</div>
      )}

      <div className="space-y-2">
        {filtered.map(x => (
          <button key={`${x.entity_type}-${x.id}`}
                  data-testid={`inbox-row-${x.entity_type}-${x.id}`}
                  onClick={() => openDetail(x)}
                  className="w-full text-left border rounded-lg p-3 bg-white hover:bg-neutral-50 flex items-center gap-3">
            <span className={`px-2 py-0.5 rounded text-[11px] border ${TYPE_COLOR[x.entity_type]}`}>{TYPE_LABEL[x.entity_type]}</span>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{x.title}</div>
              <div className="text-xs text-neutral-500">by {x.submitter || "—"} · {(x.created_at || "").slice(0, 16).replace("T", " ")}</div>
            </div>
            <div className="text-right">
              <div className="font-semibold">₹ {Number(x.amount || 0).toLocaleString("en-IN")}</div>
              <StatusPill status={x.status} />
            </div>
            <ChevronRight className="w-4 h-4 text-neutral-400" />
          </button>
        ))}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setDetail(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="inbox-detail-dialog">
          <DialogHeader>
            <DialogTitle>{active?.title}</DialogTitle>
            <DialogDescription>{active && TYPE_LABEL[active.entity_type]} · status: {active?.status}</DialogDescription>
          </DialogHeader>
          {!detail ? <div className="text-neutral-500 py-6">Loading detail…</div>
           : <DetailBody item={active} detail={detail} />}
          <DialogFooter className="gap-2 flex-wrap">
            {active && actionsFor(active).map(a => (
              <Button key={a.code} data-testid={`inbox-action-${a.code}`}
                      variant={a.danger ? "destructive" : "default"}
                      onClick={() => setConfirmAction(a)}>
                {a.label}
              </Button>
            ))}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm+notes dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(o) => { if (!o) { setConfirmAction(null); setNotes(""); } }}>
        <DialogContent data-testid="inbox-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{confirmAction?.label}</DialogTitle>
            <DialogDescription>
              {confirmAction?.requires_notes ? "Enter a reason for this action:" : "Confirm this action?"}
            </DialogDescription>
          </DialogHeader>
          {confirmAction?.requires_notes && (
            <div>
              <Label>Reviewer notes *</Label>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="inbox-notes-input" />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setConfirmAction(null); setNotes(""); }}>Cancel</Button>
            <Button onClick={performAction} disabled={busy} data-testid="inbox-confirm-btn">
              {busy ? "Working…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailBody({ item, detail }) {
  const rows = [];
  const push = (k, v) => v != null && v !== "" && rows.push([k, v]);
  push("Amount", `₹ ${Number(detail.amount || 0).toLocaleString("en-IN")}`);
  push("Submitter", detail.submitter_name_snapshot);
  push("Version", `v${detail.version}`);
  push("Status", detail.status);
  if (item.entity_type === "payment") {
    push("Distributor", detail.distributor_name_snapshot);
    push("Mode", detail.payment_mode);
    push("Reference", detail.reference_no);
    push("Payment date", detail.payment_date);
    push("Receipt", detail.receipt_photo_ref);
  }
  if (item.entity_type === "claim") {
    push("Type", detail.claim_type);
    push("Distributor", detail.distributor_name_snapshot);
    push("Retailer", detail.retailer_name_snapshot);
    push("Remarks", detail.remarks);
    push("Photos", (detail.photos || []).length);
  }
  if (item.entity_type === "sale_return") {
    push("Retailer", detail.retailer_name_snapshot);
    push("Distributor", detail.distributor_name_snapshot);
    push("Lines", (detail.sku_lines || []).length);
  }
  if (item.entity_type === "expense") {
    push("Type", detail.expense_type);
    push("Date", detail.expense_date);
    push("Bill photo", detail.bill_photo_ref);
    push("Remarks", detail.remarks);
  }
  return (
    <div className="space-y-1 text-sm max-h-[50vh] overflow-auto">
      <table className="w-full">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} className="border-b last:border-0">
              <td className="text-neutral-500 py-1 pr-4 w-32">{k}</td>
              <td className="py-1 font-medium">{String(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {item.entity_type === "sale_return" && (detail.sku_lines || []).length > 0 && (
        <div className="mt-3">
          <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">SKU Lines</div>
          <div className="space-y-1">
            {detail.sku_lines.map((l, i) => (
              <div key={i} className="text-xs bg-neutral-50 border rounded px-2 py-1 flex justify-between">
                <span>{l.sku_code_snapshot} — qty {l.qty} · {l.reason}</span>
                {l.remarks && <span className="text-neutral-500 italic">{l.remarks}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
